import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useCookie } from "#app";
import { useIntercomStore } from "@/store/intercomStore";
import { useFullStoryStore } from "@/store/fullStoryStore";
import { useHubspotSync } from "@/composables/useHubspotSync";

interface CookieConsent {
  consentId: string;
  version: string;
  responded: string;
  essentials: boolean;
  analytics: boolean;
  functional: boolean;
}

const CONSENT_COOKIE_NAME = "onclusive:mention:cookie-consent";
const CONSENT_VERSION = "1.1";

export const useCookieConsentStore = defineStore("cookieConsent", () => {
  // State

  const isReadyToChatDialogVisible = ref(false);
  const isPrivacyRespectedDialogVisible = ref(false);
  const isCookieSettingsDialogVisible = ref(false);

  const consentCookie = useCookie<CookieConsent | null>(CONSENT_COOKIE_NAME, {
    maxAge: 60 * 60 * 24 * 180, // 180 days (6 months)
    sameSite: "lax",
  });

  const hasRespondedToConsent = computed(() =>
    Boolean(consentCookie.value?.responded),
  );
  const areAnalyticsCookiesEnabled = computed(
    () => consentCookie.value?.analytics ?? false,
  );
  const areFunctionalCookiesEnabled = computed(
    () => consentCookie.value?.functional ?? false,
  );

  const pendingFunctionalCookiesSelection = ref(false);
  const pendingAnalyticsCookiesSelection = ref(false);

  const ESSENTIAL_COOKIES_DATA = [
    {
      name: "session_id",
      provider: "Internal",
      purpose: "cookieConsent.settingsDialog.cookiePurposes.sessionId",
    },
    {
      name: "csrf_token",
      provider: "Internal",
      purpose: "cookieConsent.settingsDialog.cookiePurposes.csrfToken",
    },
  ];

  const ANALYTICS_COOKIES_DATA = [
    {
      name: "fs_uid",
      provider: "FullStory",
      purpose: "cookieConsent.settingsDialog.cookiePurposes.fullStory",
    },
  ];

  const FUNCTIONAL_COOKIES_DATA = [
    {
      name: "intercom-session-*",
      provider: "Intercom",
      purpose: "cookieConsent.settingsDialog.cookiePurposes.intercomSession",
    },
    {
      name: "intercom-id-*",
      provider: "Intercom",
      purpose: "cookieConsent.settingsDialog.cookiePurposes.intercomHistory",
    },
    {
      name: "hubspot-*",
      provider: "HubSpot",
      purpose: "cookieConsent.settingsDialog.cookiePurposes.hubspotSupport",
    },
  ];

  // Getters

  const cookieSettingsSections = computed(() => [
    {
      id: "essential",
      title: "cookieConsent.settingsDialog.essential.title",
      description: "cookieConsent.settingsDialog.essential.description",
      iconName: "material-symbols:lock-outline",
      cookies: ESSENTIAL_COOKIES_DATA,
      hasToggle: false,
      toggleValue: false,
      handleToggleAction: () => {},
      hasBadge: true,
      badgeText: "cookieConsent.settingsDialog.essential.badge",
    },
    {
      id: "functional",
      title: "cookieConsent.settingsDialog.functionalAndSupport.title",
      description:
        "cookieConsent.settingsDialog.functionalAndSupport.description",
      iconName: undefined,
      cookies: FUNCTIONAL_COOKIES_DATA,
      hasToggle: true,
      toggleValue: pendingFunctionalCookiesSelection.value,
      handleToggleAction: () =>
        (pendingFunctionalCookiesSelection.value =
          !pendingFunctionalCookiesSelection.value),
    },
    {
      id: "analytics",
      title: "cookieConsent.settingsDialog.analytics.title",
      description: "cookieConsent.settingsDialog.analytics.description",
      iconName: undefined,
      cookies: ANALYTICS_COOKIES_DATA,
      hasToggle: true,
      toggleValue: pendingAnalyticsCookiesSelection.value,
      handleToggleAction: () =>
        (pendingAnalyticsCookiesSelection.value =
          !pendingAnalyticsCookiesSelection.value),
    },
  ]);

  // Actions

  const saveConsentState = (analytics: boolean, functional: boolean) => {
    const consentId = consentCookie.value?.consentId || crypto.randomUUID();
    consentCookie.value = {
      consentId,
      version: CONSENT_VERSION,
      responded: new Date().toISOString(),
      essentials: true,
      analytics,
      functional,
    };
  };

  const trackLogin = async () => {
    try {
      if (areFunctionalCookiesEnabled.value) {
        const { trackUserLoggedIn } = useHubspotSync();
        const route = useRoute();
        await trackUserLoggedIn(route.fullPath);
      }
    } catch (error) {
      // Silently fail in test environments or when composables are not available
      console.warn("Failed to track login:", error);
    }
  };

  const initializeAuthorizedScripts = async () => {
    const intercomStore = useIntercomStore();
    const fullStoryStore = useFullStoryStore();

    if (areFunctionalCookiesEnabled.value) {
      trackLogin();
      await intercomStore.bootIntercom();
    } else {
      intercomStore.shutdownIntercom();
    }

    if (areAnalyticsCookiesEnabled.value) {
      await fullStoryStore.bootFullStory();
    } else {
      fullStoryStore.shutdownFullStory();
    }
  };

  const confirmChatAndEnableCookies = async () => {
    saveConsentState(areAnalyticsCookiesEnabled.value, true);
    pendingFunctionalCookiesSelection.value = true;
    isReadyToChatDialogVisible.value = false;

    await initializeAuthorizedScripts();

    const intercomStore = useIntercomStore();
    intercomStore.showIntercom();
  };

  const closeReadyToChatDialog = () => {
    isReadyToChatDialogVisible.value = false;
  };

  const declineChatAndShowPrivacyRespectedDialog = () => {
    isReadyToChatDialogVisible.value = false;
    isPrivacyRespectedDialogVisible.value = true;
  };

  const openCookieSettingsDialog = () => {
    pendingFunctionalCookiesSelection.value = areFunctionalCookiesEnabled.value;
    pendingAnalyticsCookiesSelection.value = areAnalyticsCookiesEnabled.value;
    isCookieSettingsDialogVisible.value = true;
  };

  const acceptAllCookiesAndCloseSettings = async () => {
    saveConsentState(true, true);
    pendingAnalyticsCookiesSelection.value = true;
    pendingFunctionalCookiesSelection.value = true;
    isCookieSettingsDialogVisible.value = false;

    await initializeAuthorizedScripts();
  };

  const acceptEssentialCookiesOnly = async () => {
    saveConsentState(false, false);
    pendingAnalyticsCookiesSelection.value = false;
    pendingFunctionalCookiesSelection.value = false;
    isCookieSettingsDialogVisible.value = false;

    await initializeAuthorizedScripts();
  };

  const saveCookiePreferencesAndCloseSettings = async () => {
    saveConsentState(
      pendingAnalyticsCookiesSelection.value,
      pendingFunctionalCookiesSelection.value,
    );
    isCookieSettingsDialogVisible.value = false;

    await initializeAuthorizedScripts();
  };

  return {
    // State
    isReadyToChatDialogVisible,
    isPrivacyRespectedDialogVisible,
    isCookieSettingsDialogVisible,
    hasRespondedToConsent,
    areAnalyticsCookiesEnabled,
    areFunctionalCookiesEnabled,
    // Getters
    cookieSettingsSections,
    // Actions
    confirmChatAndEnableCookies,
    closeReadyToChatDialog,
    declineChatAndShowPrivacyRespectedDialog,
    openCookieSettingsDialog,
    acceptAllCookiesAndCloseSettings,
    acceptEssentialCookiesOnly,
    saveCookiePreferencesAndCloseSettings,
  };
});
